import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { runsApi, questionsApi } from '../utils/api'
import { formatProbability, formatTimeAgo, getAgentIcon } from '../utils/format'
import clsx from 'clsx'
import type { AgentRun, EvidenceItem } from '../types'

/**
 * RunDetail - Full detailed view of a prediction run
 *
 * Shows:
 * - Run header with status, timing, and consensus
 * - Market comparison (our prob vs market)
 * - Full agent reasoning chain with expandable cards
 * - Evidence sources with URLs
 * - Raw output JSON (collapsible)
 */
export function RunDetail() {
  const { runId } = useParams<{ runId: string }>()
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set(['synthesis']))
  const [showRawJson, setShowRawJson] = useState<Record<string, boolean>>({})

  const { data: run, isLoading, error } = useQuery({
    queryKey: ['run', runId],
    queryFn: () => runsApi.get(Number(runId)),
    enabled: !!runId,
    refetchInterval: (query) => query.state.data?.status === 'running' ? 3000 : false,
  })

  const { data: question } = useQuery({
    queryKey: ['question', run?.question_id],
    queryFn: () => questionsApi.get(run!.question_id),
    enabled: !!run?.question_id,
  })

  const toggleAgent = (agentName: string) => {
    setExpandedAgents(prev => {
      const next = new Set(prev)
      if (next.has(agentName)) {
        next.delete(agentName)
      } else {
        next.add(agentName)
      }
      return next
    })
  }

  const toggleRawJson = (agentName: string) => {
    setShowRawJson(prev => ({ ...prev, [agentName]: !prev[agentName] }))
  }

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full border-2 border-signal-blue/30 border-t-signal-blue animate-spin" />
            <p className="text-star-muted font-mono text-sm">Loading run details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !run) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="glass-card p-8 text-center">
          <p className="text-signal-red font-mono mb-4">Failed to load run</p>
          <Link to="/runs" className="btn-secondary">
            ← Back to Runs
          </Link>
        </div>
      </div>
    )
  }

  // Calculate edge if we have consensus
  const ourProb = run.consensus?.post_market_prob
  const marketProb = run.market_snapshot?.polymarket_prob as number | undefined
  const edge = ourProb !== undefined && marketProb !== undefined ? ourProb - marketProb : null

  // Determine decision
  let decision: 'buy' | 'sell' | 'hold' = 'hold'
  if (edge !== null && Math.abs(edge) > 0.02) {
    decision = edge > 0 ? 'buy' : 'sell'
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm text-star-muted">
        <Link to="/runs" className="hover:text-star transition-colors">Runs</Link>
        <span>→</span>
        <span className="text-star font-mono">Run #{run.id}</span>
      </div>

      {/* Header Card */}
      <div className="glass-card p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Left: Title and meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <RunStatusBadge status={run.status} />
              {decision !== 'hold' && (
                <DecisionBadge decision={decision} edge={edge} />
              )}
            </div>

            <h1 className="text-xl font-display font-semibold text-star mb-2 line-clamp-2">
              {question?.title || `Question #${run.question_id}`}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-star-muted">
              <span className="font-mono">Run #{run.id}</span>
              {run.started_at && (
                <span>Started {formatTimeAgo(run.started_at)}</span>
              )}
              {run.ended_at && run.started_at && (
                <span>
                  Duration: {formatDuration(new Date(run.started_at), new Date(run.ended_at))}
                </span>
              )}
              {run.orchestrator_version && (
                <span className="text-xs bg-void-100 px-2 py-0.5 rounded">
                  v{run.orchestrator_version}
                </span>
              )}
            </div>
          </div>

          {/* Right: Quick stats */}
          {run.consensus && (
            <div className="flex gap-6 lg:gap-8">
              <div className="text-center">
                <div className="text-xs text-star-muted mb-1">Our Prob</div>
                <div className="text-2xl font-mono font-semibold text-signal-blue">
                  {formatProbability(run.consensus.post_market_prob)}
                </div>
              </div>
              {marketProb !== undefined && (
                <div className="text-center">
                  <div className="text-xs text-star-muted mb-1">Market</div>
                  <div className="text-2xl font-mono text-star">
                    {formatProbability(marketProb)}
                  </div>
                </div>
              )}
              {edge !== null && (
                <div className="text-center">
                  <div className="text-xs text-star-muted mb-1">Edge</div>
                  <div className={clsx(
                    'text-2xl font-mono font-semibold',
                    edge > 0.02 ? 'text-signal-green' : edge < -0.02 ? 'text-signal-red' : 'text-star-muted'
                  )}>
                    {edge > 0 ? '+' : ''}{(edge * 100).toFixed(1)}%
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Consensus notes */}
        {run.consensus?.notes && (
          <div className="mt-4 p-3 bg-void-50 rounded-lg border border-star-dim/20">
            <div className="text-xs text-star-muted mb-1">Consensus Notes</div>
            <p className="text-sm text-star">{run.consensus.notes}</p>
          </div>
        )}
      </div>

      {/* Agent Reasoning Chain */}
      <div className="mb-6">
        <h2 className="text-lg font-display font-semibold text-star mb-4">
          Agent Reasoning Chain
        </h2>

        <div className="space-y-3">
          {run.agent_runs.map((agent, index) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              index={index}
              isExpanded={expandedAgents.has(agent.agent_name)}
              showRawJson={showRawJson[agent.agent_name] || false}
              onToggle={() => toggleAgent(agent.agent_name)}
              onToggleJson={() => toggleRawJson(agent.agent_name)}
            />
          ))}
        </div>
      </div>

      {/* Market Snapshot */}
      {run.market_snapshot && Object.keys(run.market_snapshot).length > 0 && (
        <div className="glass-card p-4 mb-6">
          <h3 className="text-sm font-mono text-star-muted mb-3">Market Snapshot at Run Time</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {run.market_snapshot.polymarket_prob !== undefined && (
              <div>
                <div className="text-xs text-star-dim">Polymarket Prob</div>
                <div className="font-mono text-star">
                  {formatProbability(run.market_snapshot.polymarket_prob as number)}
                </div>
              </div>
            )}
            {run.market_snapshot.bid !== undefined && (
              <div>
                <div className="text-xs text-star-dim">Bid</div>
                <div className="font-mono text-star">
                  {((run.market_snapshot.bid as number) * 100).toFixed(1)}¢
                </div>
              </div>
            )}
            {run.market_snapshot.ask !== undefined && (
              <div>
                <div className="text-xs text-star-dim">Ask</div>
                <div className="font-mono text-star">
                  {((run.market_snapshot.ask as number) * 100).toFixed(1)}¢
                </div>
              </div>
            )}
            {run.market_snapshot.volume_24h !== undefined && (
              <div>
                <div className="text-xs text-star-dim">24h Volume</div>
                <div className="font-mono text-star">
                  ${(run.market_snapshot.volume_24h as number).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Back link */}
      <div className="flex justify-between items-center">
        <Link to="/runs" className="btn-secondary">
          ← Back to Runs
        </Link>
        {question && (
          <Link to={`/question/${run.question_id}`} className="btn-primary">
            View Question Details →
          </Link>
        )}
      </div>
    </div>
  )
}

// Status badge component
function RunStatusBadge({ status }: { status: string }) {
  return (
    <span className={clsx(
      'px-2 py-1 rounded-full text-xs font-mono',
      status === 'completed' && 'bg-signal-green/20 text-signal-green',
      status === 'running' && 'bg-signal-blue/20 text-signal-blue',
      status === 'failed' && 'bg-signal-red/20 text-signal-red'
    )}>
      {status === 'running' && (
        <span className="inline-block w-2 h-2 bg-signal-blue rounded-full mr-1.5 animate-pulse" />
      )}
      {status.toUpperCase()}
    </span>
  )
}

// Decision badge
function DecisionBadge({ decision, edge }: { decision: 'buy' | 'sell' | 'hold'; edge: number | null }) {
  if (decision === 'hold') return null

  return (
    <span className={clsx(
      'px-2 py-1 rounded text-xs font-mono font-semibold',
      decision === 'buy' && 'bg-signal-green/20 text-signal-green border border-signal-green/30',
      decision === 'sell' && 'bg-signal-red/20 text-signal-red border border-signal-red/30'
    )}>
      {decision.toUpperCase()}
      {edge !== null && ` (${edge > 0 ? '+' : ''}${(edge * 100).toFixed(1)}%)`}
    </span>
  )
}

// Agent card with full details
function AgentCard({
  agent,
  index,
  isExpanded,
  showRawJson,
  onToggle,
  onToggleJson,
}: {
  agent: AgentRun
  index: number
  isExpanded: boolean
  showRawJson: boolean
  onToggle: () => void
  onToggleJson: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="glass-card overflow-hidden"
    >
      {/* Header - always visible */}
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 hover:bg-void-50/50 transition-colors text-left"
      >
        {/* Icon */}
        <div className={clsx(
          'w-10 h-10 rounded-full flex items-center justify-center text-lg',
          'bg-signal-green/20'
        )}>
          {getAgentIcon(agent.agent_name)}
        </div>

        {/* Name and summary */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-sm text-star capitalize">
              {agent.agent_name}
            </span>
            {agent.evidence_count > 0 && (
              <span className="text-xs bg-void-100 text-star-muted px-1.5 py-0.5 rounded">
                {agent.evidence_count} sources
              </span>
            )}
          </div>
          {agent.summary && (
            <p className={clsx(
              'text-sm text-star-muted',
              !isExpanded && 'line-clamp-1'
            )}>
              {agent.summary}
            </p>
          )}
        </div>

        {/* Probability - only show for synthesis agent */}
        {agent.agent_name === 'synthesis' &&
         agent.prob !== null && agent.prob !== undefined && (
          <div className="text-right">
            <div className="text-lg font-mono font-semibold text-signal-blue">
              {formatProbability(agent.prob)}
            </div>
            {agent.confidence !== null && agent.confidence !== undefined && agent.confidence > 0 && (
              <div className="text-xs text-star-dim">
                {(agent.confidence * 100).toFixed(0)}% conf
              </div>
            )}
          </div>
        )}

        {/* Expand indicator */}
        <span className={clsx(
          'text-star-dim transition-transform',
          isExpanded && 'rotate-180'
        )}>
          ▼
        </span>
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 border-t border-star-dim/20">
              {/* Full summary */}
              {agent.summary && (
                <div className="mt-4">
                  <div className="text-xs text-star-muted mb-1">Summary</div>
                  <p className="text-sm text-star whitespace-pre-wrap">{agent.summary}</p>
                </div>
              )}

              {/* Evidence sources */}
              {agent.evidence.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs text-star-muted mb-2">Evidence Sources</div>
                  <div className="space-y-2">
                    {agent.evidence.map((ev) => (
                      <EvidenceCard key={ev.id} evidence={ev} />
                    ))}
                  </div>
                </div>
              )}

              {/* Output JSON toggle */}
              {agent.output_json && Object.keys(agent.output_json).length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleJson(); }}
                    className="text-xs text-star-muted hover:text-star transition-colors"
                  >
                    {showRawJson ? '▼ Hide' : '▶ Show'} Raw Output JSON
                  </button>

                  <AnimatePresence>
                    {showRawJson && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <pre className="mt-2 p-3 bg-void rounded-lg text-xs font-mono text-star-muted overflow-x-auto max-h-96 overflow-y-auto">
                          {JSON.stringify(agent.output_json, null, 2)}
                        </pre>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Meta info */}
              <div className="mt-4 flex flex-wrap gap-4 text-xs text-star-dim">
                {agent.completed_at && (
                  <span>Completed {formatTimeAgo(agent.completed_at)}</span>
                )}
                {agent.prompt_version && (
                  <span>Prompt v{agent.prompt_version}</span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

// Evidence card
function EvidenceCard({ evidence }: { evidence: EvidenceItem }) {
  const domain = evidence.source_url ? extractDomain(evidence.source_url) : null

  return (
    <div className="p-3 bg-void-50 rounded-lg border border-star-dim/20">
      <div className="flex items-start gap-2">
        {/* Source type icon */}
        <span className="text-sm">
          {evidence.source_type === 'news' && '📰'}
          {evidence.source_type === 'web' && '🌐'}
          {evidence.source_type === 'api' && '🔌'}
          {!evidence.source_type && '📄'}
        </span>

        <div className="flex-1 min-w-0">
          {/* Title or URL */}
          {evidence.source_url ? (
            <a
              href={evidence.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-signal-blue hover:underline line-clamp-1"
            >
              {evidence.title || domain || evidence.source_url}
            </a>
          ) : (
            <span className="text-sm text-star">
              {evidence.title || 'Unknown source'}
            </span>
          )}

          {/* Snippet */}
          {evidence.snippet && (
            <p className="text-xs text-star-muted mt-1 line-clamp-3">
              {evidence.snippet}
            </p>
          )}

          {/* Domain badge */}
          {domain && (
            <span className="inline-block mt-1 text-xs text-star-dim bg-void-100 px-1.5 py-0.5 rounded">
              {domain}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// Helper: extract domain from URL
function extractDomain(url: string): string | null {
  try {
    return new URL(url).hostname.replace('www.', '')
  } catch {
    return null
  }
}

// Helper: format duration between two dates
function formatDuration(start: Date, end: Date): string {
  const diffMs = end.getTime() - start.getTime()
  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  }
  return `${seconds}s`
}
